import { Button, HStack, useColorModeValue } from '@chakra-ui/react';
import { t } from '@lingui/core/macro';
import { useMemo, ReactElement } from 'react';
import { SortOrder } from '@duke-hq/lib/pagination';
import type { DateRangeKey } from '@duke-hq/svc-api/provider/trpc/routers/tenant/groupRewards/utils';

interface SortFilters {
  orderBy: string | undefined;
  sort: SortOrder | undefined;
  dateRange: DateRangeKey | undefined;
}

interface DateRangeFilterButtonsProps {
  filters: SortFilters;
  onFilterChange: (filters: Partial<SortFilters>) => void;
}

export function DateRangeFilterButtons({
  filters,
  onFilterChange,
}: DateRangeFilterButtonsProps): ReactElement {
  const activeTabBg = useColorModeValue('purple.100', 'purple.50');

  const filterOptions = useMemo<{ label: string; dateRange: DateRangeKey | undefined }[]>(
    () => [
      { label: t`Last month`, dateRange: 'lastMonth' },
      { label: t`This month`, dateRange: 'currentMonth' },
      { label: t`6 months`, dateRange: 'last6Months' },
      { label: t`All`, dateRange: undefined },
    ],
    [],
  );

  const handleFilterClick = (dateRange: DateRangeKey | undefined) => {
    onFilterChange({ dateRange });
  };

  return (
    <HStack spacing={2}>
      {filterOptions.map((option) => {
        const isActive = filters.dateRange === option.dateRange;

        return (
          <Button
            key={option.dateRange ?? 'all'}
            size="xs"
            variant="tertiary"
            onClick={() => handleFilterClick(option.dateRange)}
            bg={isActive ? activeTabBg : 'transparent'}
            color={isActive ? 'fg.accent' : 'inherit'}
            _hover={{
              bg: isActive ? activeTabBg : 'gray.100',
            }}
            borderRadius="md"
          >
            {option.label}
          </Button>
        );
      })}
    </HStack>
  );
}
